import React, { useState, useRef, useCallback, useEffect } from "react";
import {
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Stack,
  IconButton,
  Paper,
  useTheme,
  useMediaQuery,
  Grid,
  Tabs,
  Tab,
  Fade,
  Divider,
  Chip,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  Radio,
  Select,
  MenuItem,
  InputLabel,
} from "@mui/material";
import VideocamIcon from "@mui/icons-material/Videocam";
import StopCircleIcon from "@mui/icons-material/StopCircle";
import CameraswitchIcon from "@mui/icons-material/Cameraswitch";
import ReplayIcon from "@mui/icons-material/Replay";
import SendIcon from "@mui/icons-material/Send";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import DeleteIcon from "@mui/icons-material/Delete";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import VideoProcessor from "./VideoProcessor";
import { CustomPage } from "./CardsPage";
import ResponsiveLayout from "../ResponsiveLayout";
import { useSnackbar } from "../../context/Snackbar";
import ImageUpload from "./ImageUpload";
import * as Sentry from "@sentry/react";
import axios from "axios";

const MAX_DURATION_SEC = 15;

const VideoCardScanner = ({ companyData, onLogout, currentUser }) => {
  const { showWarning, showSuccess, showError } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const API_BASE = import.meta.env.VITE_API_BASE;

  const videoRef = useRef(null);
  const mediaRecorderRef = useRef(null);

  // ============== VIDEO QUEUE STATE ==============
  const [videos, setVideos] = useState([]);
  const [processingVideoId, setProcessingVideoId] = useState(null);

  // ============== RECORDING STATE ==============
  const [isRecording, setIsRecording] = useState(false);
  const [stream, setStream] = useState(null);
  const [facingMode, setFacingMode] = useState("environment");
  const [elapsedSec, setElapsedSec] = useState(0);

  // ============== UI STATE ==============
  const [error, setError] = useState("");
  const [geminiResponse, setGeminiResponse] = useState(null);
  const [activeTab, setActiveTab] = useState({ id: 0, name: "video" });
  const [sendingEmailTo, setSendingEmailTo] = useState(null);

  // ============== INTERESTS & MODAL STATE ==============
  const [selectedInterests, setSelectedInterests] = useState([]);
  const [otherInterest, setOtherInterest] = useState("");
  const [additionalNotes, setAdditionalNotes] = useState("");
  const [isOptionsModalOpen, setIsOptionsModalOpen] = useState(false);
  const [pendingVideoId, setPendingVideoId] = useState(null);
  const [pendingImageProcess, setPendingImageProcess] = useState(false);

  const interestOptions = [
    "Trademind AI",
    "AI suite",
    "CRM",
    "Whatsapp Bot",
    "Website",
    "Card vision",
    "Other",
  ];

  const handleInterestChange = (interest) => {
    setSelectedInterests((prev) =>
      prev.includes(interest)
        ? prev.filter((i) => i !== interest)
        : [...prev, interest]
    );
  };

  const handleOpenOptionsModal = (videoId = null, isImage = false) => {
    setPendingVideoId(videoId);
    setPendingImageProcess(isImage);
    setIsOptionsModalOpen(true);
  };

  const handleConfirmProcessing = () => {
    setIsOptionsModalOpen(false);
    if (pendingVideoId) {
      startProcessing(pendingVideoId);
    } else if (pendingImageProcess) {
      // For image upload, we need a way to trigger it.
      // I'll use a ref or a state to trigger ImageUpload's process method.
      setTriggerImageProcess(Date.now());
    }
    // Reset pending states
    setPendingVideoId(null);
    setPendingImageProcess(false);
  };

  const clearScanOptions = () => {
    setSelectedInterests([]);
    setOtherInterest("");
    setAdditionalNotes("");
  };

  const [triggerImageProcess, setTriggerImageProcess] = useState(0);

  // ============== SEND MODAL STATE ==============
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [sendType, setSendType] = useState("");
  const [sendMethod, setSendMethod] = useState("dynamic");
  const [selectedCardForSend, setSelectedCardForSend] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [isFetchingTemplates, setIsFetchingTemplates] = useState(false);

  const fetchTemplates = useCallback(async () => {
    setIsFetchingTemplates(true);
    try {
      const response = await axios.post(`${API_BASE}/get-templates`, {}, { withCredentials: true });
      if (response.data.success) {
        setTemplates(response.data.templates);
      }
    } catch (err) {
      console.error("Failed to fetch templates:", err);
    } finally {
      setIsFetchingTemplates(false);
    }
  }, [API_BASE]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleOpenSendModal = (card, type) => {
    setSelectedCardForSend(card);
    setSendType(type);
    setSendModalOpen(true);
    setSendMethod("dynamic");
    setSelectedTemplateId("");
  };

  const fillPlaceholders = (text, card, user, companyData) => {
    let filled = text;
    // Client placeholders
    filled = filled.replace(/{{client_name}}/g, card.name || "");
    filled = filled.replace(/{{client_company}}/g, card.company || "");
    filled = filled.replace(/{{client_title}}/g, card.title || "");
    filled = filled.replace(/{{client_email}}/g, card.email || "");
    filled = filled.replace(/{{client_phone}}/g, card.phone || "");
    filled = filled.replace(/{{client_website}}/g, card.website || "");

    // User placeholders
    filled = filled.replace(/{{name}}/g, user?.name || "");
    filled = filled.replace(/{{company}}/g, companyData?.companyName || "");
    filled = filled.replace(/{{role}}/g, companyData?.role || "");
    filled = filled.replace(/{{email}}/g, user?.email || "");
    filled = filled.replace(/{{phone}}/g, user?.mobileNumber || "");
    filled = filled.replace(/{{website}}/g, companyData?.companyUrl || "");

    return filled;
  };

  const handleConfirmSend = () => {
    if (!selectedCardForSend) return;

    if (sendMethod === "dynamic") {
      if (sendType === "email") {
        window.location.href = selectedCardForSend.mailto;
      } else {
        window.location.href = selectedCardForSend.whatsapp;
      }
      setSendModalOpen(false);
    } else {
      const template = templates.find((t) => t.id === selectedTemplateId);
      if (!template) {
        alert("Please select a template.");
        return;
      }

      const filledBody = fillPlaceholders(template.body, selectedCardForSend, currentUser, companyData);

      if (sendType === "email") {
        const subject = encodeURIComponent(template.title);
        const body = encodeURIComponent(filledBody);
        window.location.href = `mailto:${selectedCardForSend.email}?subject=${subject}&body=${body}`;
      } else {
        // Handle potential array of phones
        const rawPhone = Array.isArray(selectedCardForSend.phone) ? selectedCardForSend.phone[0] : selectedCardForSend.phone;
        const phone = rawPhone?.replace(/[^0-9]/g, "");
        const body = encodeURIComponent(filledBody);
        window.open(`https://wa.me/${phone}?text=${body}`, "_blank");
      }
      setSendModalOpen(false);
    }
  };
  const recordedChunksRef = useRef([]);

  // Global state refs that NEVER get stale
  const globalState = useRef({
    isRecording: false,
    mediaRecorder: null,
    autoStopTimeout: null,
    uiInterval: null,
    startTime: null,
    mediaStream: null,
  });

  // ============== HELPER FUNCTIONS ==============

  const isValidCard = (card) => {
    if (!card || typeof card !== "object") return false;
    const hasName = card.name && typeof card.name === "string" && card.name.trim() !== "";
    const hasEmail = card.email && typeof card.email === "string" && card.email.trim() !== "";
    let hasPhone = false;
    if (Array.isArray(card.phone)) {
      hasPhone = card.phone.some((p) => p && typeof p === "string" && p.trim() !== "");
    } else if (typeof card.phone === "string") {
      hasPhone = card.phone.trim() !== "";
    }
    return hasName || hasEmail || hasPhone;
  };

  const formatTime = (s) => {
    const mm = String(Math.floor(s / 60)).padStart(2, "0");
    const ss = String(Math.floor(s % 60)).padStart(2, "0");
    return `${mm}:${ss}`;
  };

  const generateVideoId = () => `video_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  // ============== CLEANUP FUNCTION ==============
  const forceStopEverything = useCallback(() => {
    if (globalState.current.autoStopTimeout) {
      clearTimeout(globalState.current.autoStopTimeout);
      globalState.current.autoStopTimeout = null;
    }
    if (globalState.current.uiInterval) {
      clearInterval(globalState.current.uiInterval);
      globalState.current.uiInterval = null;
    }

    if (
      globalState.current.mediaRecorder &&
      globalState.current.mediaRecorder.state === "recording"
    ) {
      try {
        globalState.current.mediaRecorder.stop();
      } catch (e) {
        console.warn("MediaRecorder stop error:", e);
      }
    }

    if (globalState.current.mediaStream) {
      globalState.current.mediaStream.getTracks().forEach((track) => {
        try {
          track.stop();
        } catch (e) {
          console.warn("Track stop error:", e);
        }
      });
      globalState.current.mediaStream = null;
    }

    globalState.current.isRecording = false;
    globalState.current.mediaRecorder = null;
    globalState.current.startTime = null;

    setIsRecording(false);
    setElapsedSec(0);
    setStream(null);
  }, []);

  // ============== VIDEO QUEUE MANAGEMENT ==============
  const canRecordNewVideo = () => {
    if (isRecording) return false;
    const hasRecordedVideo = videos.some(v => v.status === "recorded");
    if (hasRecordedVideo) return false;
    return true;
  };

  const getCurrentVideo = () => {
    const recorded = videos.find(v => v.status === "recorded");
    if (recorded) return recorded;

    const failed = videos.find(v => v.status === "failed");
    if (failed) return failed;

    const staged = videos.find(v => v.status === "staged");
    return staged || null;
  };

  const deleteVideo = (videoId) => {
    setVideos(prev => {
      const video = prev.find(v => v.id === videoId);
      if (video?.url) {
        URL.revokeObjectURL(video.url);
      }
      return prev.filter(v => v.id !== videoId);
    });
    showSuccess("Video deleted");
  };

  // ============== RECORDING FUNCTIONS ==============
  const stopRecording = useCallback(() => {
    forceStopEverything();
  }, [forceStopEverything]);

  const startRecording = useCallback(async () => {
    setError("");
    setGeminiResponse(null);
    setElapsedSec(0);

    if (!canRecordNewVideo()) {
      showWarning("Please process or delete the existing video first");
      return;
    }

    forceStopEverything();

    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: facingMode,
          width: { ideal: isMobile ? 640 : 1280 },
          height: { ideal: isMobile ? 480 : 720 },
        },
        audio: false,
      });

      globalState.current.mediaStream = mediaStream;
      setStream(mediaStream);

      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        videoRef.current.muted = true;
        videoRef.current.play().catch(() => { });
      }

      recordedChunksRef.current = [];

      let mediaRecorder;
      try {
        mediaRecorder = new MediaRecorder(mediaStream, { mimeType: "video/webm; codecs=vp9" });
      } catch (e1) {
        try {
          mediaRecorder = new MediaRecorder(mediaStream, { mimeType: "video/webm" });
        } catch (e2) {
          mediaRecorder = new MediaRecorder(mediaStream, { mimeType: "video/mp4" });
        }
      }

      globalState.current.mediaRecorder = mediaRecorder;
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          recordedChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(recordedChunksRef.current, {
          type: mediaRecorder.mimeType || "video/webm",
        });

        const url = URL.createObjectURL(blob);
        const videoId = generateVideoId();
        const status = processingVideoId ? "staged" : "recorded";

        setVideos((prev) => [
          ...prev,
          {
            id: videoId,
            blob: blob,
            url: url,
            status: status,
          },
        ]);

        if (status === "staged") {
          showSuccess("Video recorded! Waiting for current processing to finish...");
        } else {
          showSuccess("Video recorded successfully!");
        }

        forceStopEverything();
      };

      mediaRecorder.start();

      globalState.current.isRecording = true;
      globalState.current.startTime = Date.now();
      setIsRecording(true);

      globalState.current.uiInterval = setInterval(() => {
        if (globalState.current.startTime && globalState.current.isRecording) {
          const elapsed = Math.floor((Date.now() - globalState.current.startTime) / 1000);
          setElapsedSec(Math.min(MAX_DURATION_SEC, elapsed));
        }
      }, 100);

      globalState.current.autoStopTimeout = setTimeout(() => {
        if (
          globalState.current.isRecording &&
          globalState.current.mediaRecorder &&
          globalState.current.mediaRecorder.state === "recording"
        ) {
          showWarning("Recording stopped: Maximum 15 seconds allowed");
          try {
            globalState.current.mediaRecorder.stop();
          } catch (e) {
            console.error("Error stopping recorder:", e);
          }
          globalState.current.isRecording = false;
          setIsRecording(false);
        }
      }, MAX_DURATION_SEC * 1000);
    } catch (err) {
      forceStopEverything();
      if (err.name === "NotAllowedError" || err.name === "PermissionDeniedError") {
        setError("Camera permission denied. Please allow camera access in your browser settings.");
      } else if (err.name === "NotFoundError" || err.name === "DevicesNotFoundError") {
        setError("No camera found. Please ensure a camera is connected and enabled.");
      } else {
        setError(`Error starting recording: ${err.message}`);
      }
    }
  }, [facingMode, isMobile, showWarning, showSuccess, forceStopEverything, isRecording, processingVideoId]);

  const toggleFacingMode = useCallback(() => {
    setFacingMode((prev) => (prev === "user" ? "environment" : "user"));
    if (globalState.current.isRecording || stream) {
      forceStopEverything();
      setTimeout(() => startRecording(), 100);
    }
  }, [forceStopEverything, startRecording, stream]);

  // ============== PROCESSING FUNCTIONS ==============

  const startProcessing = (videoId) => {
    const video = videos.find(v => v.id === videoId);
    if (!video) return;

    if (processingVideoId) {
      showWarning("Please wait — another video is currently being processed.");
      return;
    }

    setVideos(prev => prev.map(v => v.id === videoId ? { ...v, status: "processing" } : v));
    setProcessingVideoId(videoId);
  };

  const handleProcessingComplete = (videoId, contacts) => {
    console.log("✅ Processing complete for video:", videoId);

    // 🛠️ DATA SANITIZATION FIX: Ensure fields that should be strings are strictly strings
    const safeString = (val) => {
      if (Array.isArray(val)) return val.filter(Boolean).join(", ");
      if (typeof val === "string") return val;
      return "";
    };

    const sanitizedContacts = (Array.isArray(contacts) ? contacts : []).map((card) => ({
      ...card,
      name: safeString(card.name),
      email: safeString(card.email),
      title: safeString(card.title),
      company: safeString(card.company),
      website: safeString(card.website),
      address: safeString(card.address),
    }));

    const validCards = sanitizedContacts.filter(isValidCard);
    setGeminiResponse(validCards);
    setProcessingVideoId(null);

    setVideos(prev => {
      const updatedVideos = prev.map(v => v.id === videoId ? { ...v, status: "done" } : v);
      const stagedVideo = updatedVideos.find(v => v.status === "staged");
      if (stagedVideo) {
        return updatedVideos.map(v => v.id === stagedVideo.id ? { ...v, status: "recorded" } : v);
      }
      return updatedVideos;
    });

    clearScanOptions();
    showSuccess("Processing complete!");
  };

  const handleProcessingFailed = (videoId, errorMessage) => {
    console.error("❌ Processing failed for video:", videoId, errorMessage);
    setVideos(prev => prev.map(v => v.id === videoId ? { ...v, status: "failed", error: errorMessage } : v));
    setProcessingVideoId(null);
    showError(errorMessage || "Processing failed");
  };

  const retryProcessing = (videoId) => {
    const video = videos.find(v => v.id === videoId);
    if (!video || video.status !== "failed") return;

    if (processingVideoId) {
      showWarning("Please wait — another video is currently being processed.");
      return;
    }

    setVideos(prev => prev.map(v => v.id === videoId ? { ...v, status: "processing", error: undefined } : v));
    setProcessingVideoId(videoId);
  };

  // ============== TAB & IMAGE UPLOAD ==============
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue === 0 ? { id: 0, name: "video" } : { id: 1, name: "image" });
    setGeminiResponse(null);
    setVideos([]);
    setError("");
    forceStopEverything();
  };

  const handleImageProcessResults = (contacts) => {
    if (!Array.isArray(contacts)) {
      setGeminiResponse([]);
      return;
    }
    const validCards = contacts.filter(isValidCard);
    setGeminiResponse(validCards);

    if (validCards.length > 0) {
      clearScanOptions();
      showSuccess("Images processed successfully!");
    }
  };

  // ============== EMAIL FUNCTIONS ==============
  const sendEmailToContact = async (contact) => {
    if (!contact?.email || !contact.generatedEmail) {
      alert("Missing data for sending email.");
      return;
    }
    setSendingEmailTo(contact.email);
    const formData = new FormData();
    formData.append("to", contact.email);
    formData.append("subject", contact.generatedEmail.subject);
    formData.append("body", contact.generatedEmail.body);
    if (companyData?.attachments?.length > 0) {
      companyData.attachments.forEach((filename) => {
        formData.append("attachmentFilenames[]", filename);
      });
    }
    try {
      const res = await axios.post(`${API_BASE}/send-email`, formData, { withCredentials: true });
      const result = res.data;

      if (result.success) {
        alert(`✅ Email sent to ${contact.name}`);
      } else {
        alert(`❌ Failed to send email: ${result.error}`);
      }
    } catch (err) {
      if (err.response && err.response.data && err.response.data.message) {
        alert(`❌ Failed to send email: ${err.response.data.message}`);
      } else {
        alert("❌ Error sending email. Please try again later.");
      }
      if (err.response && err.response.status >= 500) {
        Sentry.captureException(err, {
          tags: { section: "video-card-scanner", source: "send-email" },
          extra: { status: err.response.status, response: err.response.data, endpoint: `${API_BASE}/send-email` },
        });
      }
    } finally {
      setSendingEmailTo(null);
    }
  };

  // ============== CLEANUP ON UNMOUNT ==============
  useEffect(() => {
    return () => {
      forceStopEverything();
      videos.forEach(video => {
        if (video.url) {
          URL.revokeObjectURL(video.url);
        }
      });
    };
  }, [forceStopEverything]);

  // ============== RENDER FUNCTIONS ==============
  const currentVideo = getCurrentVideo();
  const isProcessing = !!processingVideoId;
  const hasRecordedVideo = videos.some(v => v.status === "recorded");

  const renderVideoStatusChip = () => {
    if (!currentVideo) return null;

    let label = "";
    let color = "default";
    let icon = null;

    switch (currentVideo.status) {
      case "recorded":
        label = "Ready to Process";
        color = "success";
        icon = <CheckCircleOutlineIcon />;
        break;
      case "staged":
        label = "Waiting in Queue";
        color = "warning";
        icon = <HourglassEmptyIcon />;
        break;
      case "failed":
        label = "Processing Failed";
        color = "error";
        icon = <ErrorOutlineIcon />;
        break;
      default:
        return null;
    }

    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 1, mb: 2 }}>
        <Chip icon={icon} label={label} color={color} size="small" sx={{ fontWeight: 600 }} />
      </Box>
    );
  };

  const renderControls = () => {
    const canRecord = canRecordNewVideo() && !isRecording;

    if (isRecording) {
      return (
        <Stack direction={isMobile ? "column" : "row"} spacing={2} justifyContent="center" alignItems="center" sx={{ mt: 2 }}>
          <Button
            variant="contained"
            color="error"
            startIcon={<StopCircleIcon />}
            onClick={stopRecording}
            size={isMobile ? "medium" : "large"}
            sx={{ borderRadius: 2, fontWeight: 600 }}
          >
            Stop Recording
          </Button>
        </Stack>
      );
    }

    if (!currentVideo) {
      return (
        <Stack direction={isMobile ? "column" : "row"} spacing={2} justifyContent="center" alignItems="center" sx={{ mt: 2 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<VideocamIcon />}
            onClick={startRecording}
            disabled={!canRecord}
            size={isMobile ? "medium" : "large"}
            sx={{
              borderRadius: 2,
              fontWeight: 600,
              backgroundColor: "#6366F1",
              "&:hover": { backgroundColor: "#4F46E5" },
            }}
          >
            {isProcessing ? "Record New Video" : hasRecordedVideo ? "Process or Delete First" : "Start Recording"}
          </Button>

          {navigator.mediaDevices?.enumerateDevices && (
            <IconButton
              onClick={toggleFacingMode}
              title="Switch Camera"
              color="secondary"
              disabled={isRecording}
              size={isMobile ? "medium" : "large"}
              sx={{ backgroundColor: "#F3F4F6", "&:hover": { backgroundColor: "#E5E7EB" } }}
            >
              <CameraswitchIcon />
            </IconButton>
          )}
        </Stack>
      );
    }

    return (
      <Stack direction={isMobile ? "column" : "row"} spacing={2} justifyContent="center" alignItems="center" sx={{ mt: 2 }}>
        <Button variant="outlined" color="error" startIcon={<DeleteIcon />} onClick={() => deleteVideo(currentVideo.id)} disabled={currentVideo.status === "processing"}>
          Delete
        </Button>
        {currentVideo.status !== "processing" && canRecord && (
          <Button variant="outlined" startIcon={<ReplayIcon />} onClick={() => { deleteVideo(currentVideo.id); setTimeout(() => startRecording(), 100); }}>
            Re-record
          </Button>
        )}
        {currentVideo.status === "recorded" && (
          <Button variant="contained" color="success" startIcon={<SendIcon />} onClick={() => handleOpenOptionsModal(currentVideo.id)} disabled={isProcessing}>
            {isProcessing ? "Processing..." : "Process with AI"}
          </Button>
        )}
        {currentVideo.status === "failed" && (
          <Button variant="contained" color="primary" startIcon={<ReplayIcon />} onClick={() => retryProcessing(currentVideo.id)} disabled={isProcessing} sx={{ backgroundColor: "#6366F1", "&:hover": { backgroundColor: "#4F46E5" } }}>
            {isProcessing ? "Processing..." : "Retry Processing"}
          </Button>
        )}
        {currentVideo.status === "staged" && (
          <Button variant="outlined" disabled startIcon={<HourglassEmptyIcon />}>
            Waiting for Current Processing
          </Button>
        )}
      </Stack>
    );
  };

  return (
    <>
      <ResponsiveLayout currentUser={currentUser} onLogout={onLogout} />
      <Paper
        elevation={4}
        sx={{
          p: isMobile ? 2 : 4,
          m: "auto",
          maxWidth: 900,
          borderRadius: 4,
          mt: 5,
          mb: 5,
          background: "#FFFFFF",
          boxShadow: "0 8px 24px rgba(99,102,241,0.07)",
        }}
      >
        <Typography variant={isMobile ? "h5" : "h4"} fontWeight={800} align="center" gutterBottom sx={{ color: "#1e293b", mb: 0.5 }}>
          Card Vision
        </Typography>
        <Typography variant="subtitle1" align="center" color="text.secondary" sx={{ mb: 3, maxWidth: 520, mx: "auto" }}>
          Upload a video or image of visiting cards — our AI converts them into structured contact data instantly.
        </Typography>

        {/* ============== INTERESTS SELECTION (MOVED TO MODAL) ============== */}

        <Tabs value={activeTab.id} onChange={handleTabChange} variant="fullWidth" textColor="primary" indicatorColor="primary" sx={{ ".MuiTab-root": { borderTopLeftRadius: 4, borderTopRightRadius: 4, fontWeight: 700 } }}>
          <Tab label="Video" />
          <Tab label="Image" />
        </Tabs>
        <Divider sx={{ mb: 2 }} />

        {activeTab.name === "video" ? (
          <>
            <Box sx={{ width: "100%", aspectRatio: "16/9", backgroundColor: "#000", borderRadius: 2, overflow: "hidden", mb: 2, border: (theme) => `2px solid ${theme.palette.divider}`, position: "relative" }}>
              <video ref={videoRef} autoPlay playsInline muted style={{ width: "100%", height: "100%", objectFit: "contain" }} src={currentVideo?.url || ""} />
              {isRecording && (
                <>
                  <Box sx={{ position: "absolute", top: 10, right: 10, backgroundColor: "#ef4444", color: "#fff", px: 2, py: 0.5, borderRadius: 2, fontWeight: 700, fontSize: "0.85rem", letterSpacing: 1, boxShadow: "0 2px 6px rgba(239,68,68,0.09)", animation: "pulse 1.5s ease-in-out infinite", "@keyframes pulse": { "0%": { opacity: 1 }, "50%": { opacity: 0.7 }, "100%": { opacity: 1 } } }}>
                    REC
                  </Box>
                  <Box sx={{ position: "absolute", top: 10, left: 10, backgroundColor: "rgba(0,0,0,0.7)", color: "#fff", px: 1.25, py: 0.5, borderRadius: 2, fontWeight: 700, fontSize: "0.85rem", fontFamily: "monospace" }}>
                    {formatTime(elapsedSec)} / {formatTime(MAX_DURATION_SEC)}
                  </Box>
                </>
              )}
            </Box>

            {renderVideoStatusChip()}
            {renderControls()}

            {!isRecording && !currentVideo && (
              <Typography variant="body2" align="center" sx={{ mt: 1, color: "#3b82f6", fontWeight: 500 }}>
                ⏱️ Recording automatically stops after {MAX_DURATION_SEC} seconds
              </Typography>
            )}

            {isProcessing && (
              <Alert severity="info" sx={{ mt: 2, borderRadius: 3, backgroundColor: "#EFF6FF", color: "#1E40AF" }}>
                🤖 AI is processing your video... You can record a new video while waiting!
              </Alert>
            )}
          </>
        ) : (
          <ImageUpload
            onProcessImages={handleImageProcessResults}
            error={error}
            isMobile={isMobile}
            API_BASE={API_BASE}
            selectedInterests={selectedInterests}
            otherInterest={otherInterest}
            additionalNotes={additionalNotes}
            triggerProcess={triggerImageProcess}
            onOpenOptions={() => handleOpenOptionsModal(null, true)}
          />
        )}

        {processingVideoId && videos.find(v => v.id === processingVideoId) && (
          <VideoProcessor
            key={processingVideoId}
            videoBlob={videos.find(v => v.id === processingVideoId).blob}
            videoId={processingVideoId}
            companyData={companyData}
            selectedInterests={selectedInterests}
            otherInterest={otherInterest}
            additionalNotes={additionalNotes}
            onResult={(contacts) => handleProcessingComplete(processingVideoId, contacts)}
            onError={(errorMsg) => handleProcessingFailed(processingVideoId, errorMsg)}
          />
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2, borderRadius: 3, backgroundColor: "#FEE2E2", color: "#DC2626", border: "none", "& .MuiAlert-icon": { color: "#DC2626" } }}>
            ⚠️ {error}
          </Alert>
        )}

        {geminiResponse && (
          <Box sx={{ mt: 5 }}>
            <Typography variant="h6" fontWeight={700} gutterBottom sx={{ display: "flex", alignItems: "center", mb: 2, color: "#16a34a" }}>
              <CheckCircleOutlineIcon color="success" sx={{ mr: 1 }} /> Extracted Card Details
            </Typography>
            {geminiResponse.length > 0 ? (
              <Grid container spacing={3}>
                {geminiResponse.map((card, index) => (
                  <Grid item xs={12} sm={6} md={4} key={index} sx={{ display: "flex", justifyContent: "center" }}>
                    <Paper elevation={3} sx={{ width: 330, minHeight: 270, display: "flex", flexDirection: "column", justifyContent: "space-between", p: 2, borderRadius: 3, bgcolor: "#fff", boxShadow: "0 4px 16px rgba(99,102,241,0.09)", transition: "box-shadow 0.18s", "&:hover": { boxShadow: "0 8px 24px rgba(99,102,241,0.15)" } }}>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="subtitle1" fontWeight="bold" sx={{ wordBreak: "break-word", mb: 1 }}>{card.name || "N/A"}</Typography>
                        {card.title && <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>{card.title}</Typography>}
                        {card.company && <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{card.company}</Typography>}
                        {Array.isArray(card.phone)
                          ? card.phone.map((num, idx) => <Typography key={idx} variant="body2" sx={{ mb: 0.5 }}>Phone {card.phone.length > 1 ? `#${idx + 1}` : ""}: {num}</Typography>)
                          : card.phone && <Typography variant="body2" sx={{ mb: 0.5 }}>Phone: {card.phone}</Typography>}
                        {card.email && <Typography variant="body2" sx={{ mb: 0.5 }}>Email: {card.email}</Typography>}
                        {card.website && <Typography variant="body2" sx={{ mb: 0.5 }}>Web: {card.website}</Typography>}
                        {card.address && <Typography variant="body2" sx={{ fontSize: "0.93em", mt: 1 }}>{card.address}</Typography>}
                      </Box>
                      <Divider sx={{ my: 1 }} />
                      <Stack spacing={1}>
                        {card.generatedEmail?.mailto && (
                          <Button
                            variant="outlined"
                            color="primary"
                            size="small"
                            fullWidth
                            startIcon={<MailOutlineIcon />}
                            onClick={() => handleOpenSendModal(card, "email")}
                            sx={{ borderRadius: 2 }}
                          >
                            Open in Mail Client
                          </Button>
                        )}
                        {card.generatedEmail?.whatsapp && (
                          <Button
                            variant="outlined"
                            color="success"
                            size="small"
                            fullWidth
                            startIcon={<WhatsAppIcon />}
                            onClick={() => handleOpenSendModal(card, "whatsapp")}
                            sx={{ borderRadius: 2 }}
                          >
                            Open in WhatsApp
                          </Button>
                        )}
                        {currentUser?.is_super_admin === 1 && (
                          <Button variant="contained" color="primary" size="small" fullWidth startIcon={<SendIcon />} onClick={() => sendEmailToContact(card)} disabled={sendingEmailTo === card.email} sx={{ borderRadius: 2, backgroundColor: "#6366F1", "&:hover": { backgroundColor: "#4F46E5" } }}>
                            {sendingEmailTo === card.email ? <CircularProgress color="inherit" size={16} /> : "Send Email"}
                          </Button>
                        )}
                      </Stack>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Alert severity="info" icon={<ErrorOutlineIcon />} sx={{ borderRadius: 3, backgroundColor: "#EFF6FF", color: "#1E40AF", border: "none" }}>
                {activeTab.name === "video" ? "No card details were extracted. Try recording a clearer video or ensure cards are well-lit and in focus." : "No card details were extracted from the images. Try uploading a clearer image or ensure cards are well-lit and in focus."}
              </Alert>
            )}
          </Box>
        )}
      </Paper>
      {companyData && (
        <Box>
          <CustomPage companyData={companyData} onLogout={onLogout} />
        </Box>
      )}

      {/* ============== PROCESS OPTIONS MODAL ============== */}
      <Dialog open={isOptionsModalOpen} onClose={() => setIsOptionsModalOpen(false)} fullWidth maxWidth="sm" sx={{ "& .MuiDialog-paper": { borderRadius: 4 } }}>
        <DialogTitle sx={{ fontWeight: 800, color: "#1e293b", pb: 1 }}>
          Scan Options
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Tell us more about the lead before we process the card details.
          </Typography>

          <FormControl component="fieldset" variant="standard" fullWidth sx={{ mb: 2 }}>
            <FormLabel component="legend" sx={{ fontWeight: 700, color: "#475569", mb: 1, fontSize: "0.9rem" }}>
              Interested Products/Services
            </FormLabel>
            <Grid container spacing={0.5}>
              {interestOptions.map((option) => (
                <Grid item xs={12} sm={6} key={option}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedInterests.includes(option)}
                        onChange={() => handleInterestChange(option)}
                        size="small"
                        sx={{ color: "#94a3b8", "&.Mui-checked": { color: "#6366F1" } }}
                      />
                    }
                    label={<Typography variant="body2" sx={{ color: "#334155" }}>{option}</Typography>}
                  />
                </Grid>
              ))}
            </Grid>
          </FormControl>

          <Stack spacing={2}>
            {selectedInterests.includes("Other") && (
              <TextField
                fullWidth
                size="small"
                label="Specify Other Interest"
                placeholder="e.g. Custom Development"
                value={otherInterest}
                onChange={(e) => setOtherInterest(e.target.value)}
                variant="outlined"
              />
            )}
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Additional Notes"
              placeholder="Add any extra context about this lead..."
              value={additionalNotes}
              onChange={(e) => setAdditionalNotes(e.target.value)}
              variant="outlined"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button onClick={() => {
            clearScanOptions();
            setIsOptionsModalOpen(false);
          }} sx={{ color: "#64748b", fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmProcessing}
            startIcon={<SendIcon />}
            sx={{
              borderRadius: 2,
              fontWeight: 700,
              px: 3,
              backgroundColor: "#6366F1",
              "&:hover": { backgroundColor: "#4F46E5" },
            }}
          >
            Start AI Scan
          </Button>
        </DialogActions>
      </Dialog>

      {/* ============== SEND OPTIONS DIALOG ============== */}
      <Dialog open={sendModalOpen} onClose={() => setSendModalOpen(false)} fullWidth maxWidth="xs" sx={{ "& .MuiDialog-paper": { borderRadius: 4 } }}>
        <DialogTitle sx={{ fontWeight: 800, color: "#1e293b" }}>
          Choose Send Method
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            How would you like to send this {sendType === "email" ? "Email" : "WhatsApp"} to {selectedCardForSend?.name}?
          </Typography>

          <RadioGroup value={sendMethod} onChange={(e) => setSendMethod(e.target.value)}>
            <FormControlLabel
              value="dynamic"
              control={<Radio sx={{ "&.Mui-checked": { color: "#6366F1" } }} />}
              label={
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>Dynamic AI Message</Typography>
                  <Typography variant="caption" color="text.secondary">Use the message generated specifically for this card.</Typography>
                </Box>
              }
              sx={{ mb: 2, alignItems: "flex-start", mt: 1 }}
            />
            <FormControlLabel
              value="template"
              control={<Radio sx={{ "&.Mui-checked": { color: "#6366F1" } }} />}
              label={
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>Use from Template</Typography>
                  <Typography variant="caption" color="text.secondary">Select one of your pre-defined message templates.</Typography>
                </Box>
              }
              sx={{ alignItems: "flex-start" }}
            />
          </RadioGroup>

          {sendMethod === "template" && (
            <Box sx={{ mt: 3 }}>
              <FormControl fullWidth size="small">
                <InputLabel id="template-select-label">Select Template</InputLabel>
                <Select
                  labelId="template-select-label"
                  value={selectedTemplateId}
                  label="Select Template"
                  onChange={(e) => setSelectedTemplateId(e.target.value)}
                  disabled={isFetchingTemplates}
                >
                  {templates.length === 0 ? (
                    <MenuItem disabled>No templates found</MenuItem>
                  ) : (
                    templates.map((t) => (
                      <MenuItem key={t.id} value={t.id}>
                        {t.title}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
              {isFetchingTemplates && (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
                  <CircularProgress size={20} />
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button onClick={() => setSendModalOpen(false)} sx={{ color: "#64748b", fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmSend}
            disabled={sendMethod === "template" && !selectedTemplateId}
            sx={{
              borderRadius: 2,
              fontWeight: 700,
              px: 3,
              backgroundColor: "#6366F1",
              "&:hover": { backgroundColor: "#4F46E5" },
            }}
          >
            Confirm & Send
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default VideoCardScanner;